/**
 * Resolución de precios de inscripción de eventos.
 *
 * Reglas (en orden de precedencia):
 *  1. Si el club pertenece a un distrito con descuento (`isDiscounted=true`):
 *     - early-bird con descuento vigente → `discountedEarlyBirdAmount`
 *     - sin early-bird o vencido       → `discountedAmount`
 *     - si la categoría no define `discountedAmount`, se cae al precio normal.
 *  2. Sin descuento por distrito:
 *     - early-bird vigente → `earlyBirdAmount`
 *     - sino               → `amount`
 */

import { Timestamp } from "firebase/firestore";
import type { Event, EventPricing } from "@/types";

const isEarlyBirdActive = (
  pricing: EventPricing,
  nowMs: number = Timestamp.now().toMillis(),
): boolean =>
  pricing.earlyBirdAmount != null &&
  !!pricing.earlyBirdDeadline &&
  nowMs <= pricing.earlyBirdDeadline.toMillis();

/**
 * Calcula el monto efectivo a cobrar para una categoría de pricing dada,
 * considerando early-bird y descuento por distrito.
 */
export function getActiveAmount(
  pricing: EventPricing,
  isDiscounted: boolean = false,
  nowMs: number = Timestamp.now().toMillis(),
): number {
  const earlyBird = isEarlyBirdActive(pricing, nowMs);

  if (isDiscounted && pricing.discountedAmount != null) {
    if (earlyBird && pricing.discountedEarlyBirdAmount != null) {
      return pricing.discountedEarlyBirdAmount;
    }
    return pricing.discountedAmount;
  }

  if (earlyBird) return pricing.earlyBirdAmount!;
  return pricing.amount;
}

/**
 * Resuelve si un club califica para precio con descuento en un evento dado.
 * Requiere conocer el `districtId` resuelto del club (vía Church).
 */
export function isClubDiscounted(
  districtId: string | null | undefined,
  event: Pick<Event, "discountedDistrictIds">,
  /**
   * Decisión explícita en el club (`clubs.hasSpecialRate`), que MANDA sobre el
   * distrito. Existe porque el distrito no alcanza a expresar la realidad: en
   * dos días aparecieron tres casos de clubes que comparten distrito y no
   * comparten tarifa — GAMA y ARMOHET en la iglesia Filadelfia, BETANIA y
   * JASER en Pta Bethania. Con el distrito solo, dársela a uno se la daba al
   * otro.
   *
   * `true` la concede y `false` la niega aunque su distrito la tenga;
   * `undefined` deja decidir al distrito, que sigue siendo el caso normal.
   * Sólo un admin puede escribirlo (firestore.rules).
   */
  clubOverride?: boolean,
): boolean {
  if (typeof clubOverride === "boolean") return clubOverride;
  if (!districtId) return false;
  return !!event.discountedDistrictIds?.includes(districtId);
}

/**
 * `true` si alguno de los pricings tiene early-bird vigente en este momento.
 */
export function hasActiveEarlyBird(
  event: Pick<Event, "pricing">,
  nowMs: number = Timestamp.now().toMillis(),
): boolean {
  return event.pricing.some((p) => isEarlyBirdActive(p, nowMs));
}

/**
 * `true` si el evento define al menos un distrito con descuento configurado.
 */
export function hasDistrictDiscount(
  event: Pick<Event, "discountedDistrictIds">,
): boolean {
  return (event.discountedDistrictIds?.length ?? 0) > 0;
}
